import {
	EventIdentifier,
	FeatureTeamEvent,
	type RequirementIdentifier,
	TrackingEventType,
	useAnalytics,
	useSettings
} from '@capsicum/common';
import { useCallback } from 'react';

export enum RequirementCompletionEventsEnum {
	done = 'done',
	failed = 'failed',
	ragequit = 'ragequit',
	started = 'started'
}

export enum RequirementsPageCredentialsNeeded {
	auctionBid = 'Auction Bid',
	fanTokenOfferingBuy = 'Fan Token Offering Buy',
	marketplaceBuy = 'Marketplace Buy',
	marketplaceSell = 'Marketplace Sell',
	ncWalletTopup = 'NC Wallet Topup',
	productDetails = 'Product Details',
	sendAssetSelector = 'Send Asset Selector',
	trade = 'Trade',
	voucherRedemption = 'Voucher Redemption',
	walletReceive = 'Wallet Receive',
	walletTopUp = 'Wallet TopUp',
	walletWithdraw = 'Wallet Withdraw',
	worldChampionshipPledge = 'World Championship Pledge'
}

const EventIdentifierMap: Record<RequirementCompletionEventsEnum, EventIdentifier> = {
	[RequirementCompletionEventsEnum.done]: EventIdentifier.RequirementCompletionDone,
	[RequirementCompletionEventsEnum.failed]: EventIdentifier.RequirementCompletionFailed,
	[RequirementCompletionEventsEnum.ragequit]: EventIdentifier.RequirementCompletionRageQuit,
	[RequirementCompletionEventsEnum.started]: EventIdentifier.RequirementCompletionStarted
};

type RequirementCompletionEvents = keyof typeof RequirementCompletionEventsEnum;
type RequirementsReasons = 'REQUIREMENT_DENIED_REASON' | 'REQUIREMENT_NOT_COMPLETED_REASON';
type TrackRequirementsNeededReason = {
	page: string;
	reason: RequirementsReasons;
	requirements?: Array<RequirementIdentifier> | RequirementIdentifier;
};

export const useTrackRequirementsEvents = () => {
	const analytics = useAnalytics();
	const {
		settings: { areRequirementsAnalyticsEnabled }
	} = useSettings();

	const trackRequirementsCompletion = useCallback(
		(event: RequirementCompletionEvents, requirement: RequirementIdentifier) => {
			if (!areRequirementsAnalyticsEnabled) return;

			const requirementEventIdentifier = EventIdentifierMap[event];

			analytics.sendEvent(TrackingEventType.track, requirementEventIdentifier, {
				feature_team: FeatureTeamEvent.KRFT,
				text: `${event} ${requirement}`
			});
		},
		[analytics, FeatureTeamEvent]
	);

	const trackRequirementsNeededReason = useCallback(
		({ page, reason, requirements }: TrackRequirementsNeededReason) => {
			if (!areRequirementsAnalyticsEnabled) return;

			const requirementEventIdentifier =
				reason === 'REQUIREMENT_DENIED_REASON'
					? EventIdentifier.RequirementDenied
					: EventIdentifier.RequirementNotCompleted;

			analytics.sendEvent(TrackingEventType.track, requirementEventIdentifier, {
				feature_team: FeatureTeamEvent.KRFT,
				page: page,
				text: `${reason} ${requirements}`
			});
		},
		[analytics, FeatureTeamEvent]
	);

	return {
		trackRequirementsCompletion,
		trackRequirementsNeededReason
	};
};
